import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { noop } from 'lodash';
import { Icon } from '@fiverr-private/fit';
import { DIRECTIONS } from '../Slider/constants';
import { INDICATOR } from '../../propTypes';
import dotIcon from './icons/dot';
import { DOT_SIZES, CORE_DOT_POSITIONS, MIN_DOTS_SHOW, SIMPLE_DOTS_BREAKPOINT } from './constants';
import './index.scss';

/**
 * Checks if the specified dot index is the next to the active index,
 * depending on what's the position of the active dot in core array.
 * [ x ], [ v ], [ v ] - case 1: 1, 2;
 * [ v ], [ x ], [ v ] - case 2: -1, 1;
 * [ v ], [ v ], [ x ] - case 3: -2, -1;
 * where x - active index and v - the position current index should match to be a core dot.
 * @return {boolean}
 */
const getIsCoreDot = (currIndex, activeIndex, activeDotPosition) => {
    if (currIndex === activeIndex) {
        return true;
    }

    switch (activeDotPosition) {
        case CORE_DOT_POSITIONS.LEFT:
            return currIndex === activeIndex + 1 || currIndex === activeIndex + 2;
        case CORE_DOT_POSITIONS.CENTER:
            return currIndex === activeIndex - 1 || currIndex === activeIndex + 1;
        case CORE_DOT_POSITIONS.RIGHT:
            return currIndex === activeIndex - 1 || currIndex === activeIndex - 2;

        default:
            return false;
    }
};

/**
 * The dot gets its size depending on the distance from the core array.
 * @return {number}
 */
const getDotSize = (currIndex, coreDots) => {
    const diff =
        currIndex > coreDots[0].position
            ? currIndex - coreDots[coreDots.length - 1].position
            : coreDots[0].position - currIndex;

    switch (diff) {
        case 1:
            return DOT_SIZES.S;
        case 2:
            return DOT_SIZES.XS;
        default:
            return 0;
    }
};

/**
 * Depending on the slider direction, active dot should change it's position in the core array.
 * That would ensure this dot moving to the left and to the right while being if it was in the middle
 * without changing the sizes of other dots.
 */
const getActiveDotPosition = (direction, activeIndex, total, oldPosition) => {
    switch (direction) {
        case DIRECTIONS.BACK:
            // Infinite loop case, when user decides to go back from the 0 variation.
            if (activeIndex === total - 1) {
                return CORE_DOT_POSITIONS.RIGHT;
            }

            switch (oldPosition) {
                case CORE_DOT_POSITIONS.CENTER:
                    return CORE_DOT_POSITIONS.LEFT;
                case CORE_DOT_POSITIONS.RIGHT:
                    return CORE_DOT_POSITIONS.CENTER;

                // Being the most left dot, active dot would still be the most left dot.
                default:
                    return CORE_DOT_POSITIONS.LEFT;
            }
        case DIRECTIONS.NEXT:
            // Infinite loop case, when user decides to go from the last variation to the very beginning.
            if (activeIndex === 0) {
                return CORE_DOT_POSITIONS.LEFT;
            }

            switch (oldPosition) {
                case CORE_DOT_POSITIONS.CENTER:
                    return CORE_DOT_POSITIONS.RIGHT;
                case CORE_DOT_POSITIONS.LEFT:
                    return CORE_DOT_POSITIONS.CENTER;

                // Being the most right dot, active dot would still be the most right dot.
                default:
                    return CORE_DOT_POSITIONS.RIGHT;
            }

        default:
            return CORE_DOT_POSITIONS.LEFT;
    }
};

/**
 * Renders counter indicator as dots.
 * There are two arrays for this logic:
 * - the first one is an array of all dots;
 * - the second one represents three core dots (an active one + two more).
 * When going <-/-> we move the second array on the surface of the first one
 * and calculate dot sizes by the distance from this array, but not from the active dot.
 * @param {Number} activeIndex - index of active dot.
 * @param {Number} total - total quantity of dots.
 * @param {Function} onClick - cb.
 * @param {Number} direction - slider swipe direction.
 */
const Indicator = ({ className, activeIndex = 0, total = MIN_DOTS_SHOW, onClick = noop, direction = null }) => {
    const [activeDotPosition, setActiveDotPosition] = useState(CORE_DOT_POSITIONS.RIGHT);
    const [dots, setDots] = useState([]);

    useEffect(() => {
        if (total <= SIMPLE_DOTS_BREAKPOINT) {
            const allDots = Array.from(Array(total)).map((el, index) => ({
                position: index,
                size: index === activeIndex ? DOT_SIZES.L : DOT_SIZES.M,
                isActive: index === activeIndex,
            }));

            setDots(allDots);
        } else {
            const newActivePosition = getActiveDotPosition(direction, activeIndex, total, activeDotPosition);

            setActiveDotPosition(newActivePosition);

            const allDots = Array.from(Array(total)).map((el, index) => ({
                position: index,
                isActive: index === activeIndex,
                isCore: getIsCoreDot(index, activeIndex, newActivePosition),
            }));

            const coreDots = allDots.filter(({ isCore }) => isCore);

            const mappedDots = allDots.reduce((acc, { position, isCore, isActive }) => {
                if (isActive) {
                    acc.push({ position, isActive, size: DOT_SIZES.L });
                } else if (isCore) {
                    acc.push({ position, size: DOT_SIZES.M });
                } else {
                    acc.push({ position, size: getDotSize(position, coreDots) });
                }

                return acc;
            }, []);

            setDots(mappedDots);
        }
    }, [direction, activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className={cn(className, 'dots-indicator')} onClick={onClick}>
            {dots.map(
                (dot, index) =>
                    dot.size > 0 && (
                        <span className={cn('dot', { active: dot.isActive })} key={index}>
                            <Icon size={dot.size}>{dotIcon}</Icon>
                        </span>
                    )
            )}
        </div>
    );
};

Indicator.propTypes = INDICATOR;

export default Indicator;
